import { ApolloClient, ApolloProvider, fromPromise, HttpLink, InMemoryCache, split } from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import { getMainDefinition } from '@apollo/client/utilities'
import { InteractionRequiredAuthError } from '@azure/msal-browser'
import { useMsal } from '@azure/msal-react'
import React, { useMemo } from 'react'

// @ts-ignore
import Env from '../gen/Env'
import { LOGIN_REQUEST } from '../../common/hooks/auth'
import { logoutOnce } from '../../common/hooks/useLogout'
import { renewSession } from '../../common/auth/sessionRenewal'
import { SSELink } from './ApolloSSESubscriptionLink'
import { handleCurvoError, hasUnauthenticatedError } from './errorHandler'

export const ApolloClientWrapper: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { instance, accounts } = useMsal()
  const subscriptionClient = React.useRef<SSELink>()

  const client = useMemo(() => {
    const account = accounts[0]
    if (!instance || !account) {
      return undefined
    }

    // Always call acquireTokenSilent — MSAL caches tokens internally and proactively
    // refreshes them before expiry, so there is no need to cache the token ourselves.
    // Caching it in a ref causes stale-token errors when the token expires mid-session.
    const getToken = async (forceRefresh = false) => {
      const result = await instance.acquireTokenSilent({ account, ...LOGIN_REQUEST, forceRefresh })
      return result.idToken
    }

    const getHeader = async () => {
      const token = await getToken()
      return { Authorization: `Bearer ${token}` }
    }

    // Single-flight (see common/hooks/useLogout): errorLink runs per operation, so a batch of
    // requests resuming after an idle period would otherwise start one logout each and MSAL
    // would reject all but the first with interaction_in_progress.
    const logout = () => logoutOnce(instance)

    // Inactivity logout lives in useIdleLogout (common/hooks), driven by user activity
    // rather than GraphQL traffic — see docs/specs/7570-idle-auto-logout/spec.md.
    const httpLink = new HttpLink({ uri: Env.api })

    subscriptionClient.current = new SSELink({
      url: Env.api.replace('/graphql', '/subscriptions'),
      headers: getHeader,
    })

    const wsLink = subscriptionClient.current

    const splitLink = split(
      ({ query }: any) => {
        const definition = getMainDefinition(query)
        return definition.kind === 'OperationDefinition' && definition.operation === 'subscription'
      },
      wsLink,
      httpLink,
    )

    const authLink = setContext(async (_, { headers }) => {
      try {
        const authHeader = await getHeader()
        return { headers: { ...headers, ...authHeader } }
      } catch (error) {
        // InteractionRequiredAuthError means the refresh token hit Entra's 24-hour SPA lifetime.
        // The Entra session cookie is usually still good, so renew silently and carry on with the
        // fresh token; renewSession falls back to an interactive sign-in — never a sign-out — when
        // the silent attempt fails, so an expired session no longer ejects a mid-task user (#7665).
        if (error instanceof InteractionRequiredAuthError) {
          const token = await renewSession(instance, account)
          return token ? { headers: { ...headers, Authorization: `Bearer ${token}` } } : { headers }
        }
        // Other MSAL errors (interaction_in_progress, reinitialization during HMR, etc.)
        // are transient — let the request through and let the error link retry.
        console.error('acquireTokenSilent failed in authLink (transient):', error)
        return { headers }
      }
    })

    const errorLink = onError(error => {
      if (hasUnauthenticatedError(error)) {
        const { operation, forward } = error
        return fromPromise(
          getToken(true).catch(async err => {
            console.error(err)
            // The same 24-hour expiry authLink handles can surface here instead: authLink's cached
            // token is rejected by the server, then the forced refresh hits the expired refresh
            // token. Renew rather than eject, as authLink does (ADR 0001, PR 7037 review).
            if (err instanceof InteractionRequiredAuthError) {
              return (await renewSession(instance, account)) ?? ''
            }
            logout()
            return ''
          }),
        )
          .filter(value => Boolean(value))
          .flatMap(at => {
            operation.setContext({
              headers: {
                ...operation.getContext().headers,
                Authorization: `Bearer ${at}`,
              },
            })
            return forward(operation)
          })
      } else {
        console.error('Original error', error)
        handleCurvoError(error)
      }
      return
    })

    return new ApolloClient({
      link: authLink.concat(errorLink).concat(splitLink),
      cache: new InMemoryCache(),
      resolvers: {},
    })
  }, [instance, accounts])

  if (!client) {
    return <>loading...</>
  }

  return <ApolloProvider client={client}>{children}</ApolloProvider>
}
